import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ContactFooter() {
  const ref = useScrollReveal<HTMLDivElement>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const quickLinks = [
    { label: t('footer.quickServices'), href: '#services' },
    { label: t('footer.quickHowToOperate'), href: '#operate' },
    { label: t('footer.quickRegulations'), href: '#regulations' },
    { label: t('footer.quickBookSession'), href: '#consultation' },
  ];

  const hours = t('footer.hours', { returnObjects: true }) as { day: string; hours: string }[];

  const handleNav = (href: string) => {
    if (location.pathname === '/') {
      // Already on home page — scroll directly
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // On another page — navigate to home with hash
      navigate('/' + href);
    }
  };

  return (
    <footer id="contact" className="bg-white text-gray-900">
      {/* Top Section */}
      <div ref={ref} className="max-w-7xl mx-auto px-6 pt-16 pb-12 reveal-on-scroll animate-fade-in-up">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl">
                <img
                  src="https://static.readdy.ai/image/aa2be49284c025d1769847343deb2d78/1ae291200abc8aebd605c69261db31da.png"
                  alt="My Food Truck Agency Logo"
                  className="w-9 h-9 object-contain"
                />
              </div>
              <div>
                <div className="font-bold text-gray-900 text-sm">My Food Truck</div>
                <div className="text-teal-500 font-bold text-sm">Agency</div>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-5">
              {t('footer.brandDesc')}
            </p>
            <div className="flex flex-col gap-2.5">
              <a href="mailto:myfoodtruckagency@gmail.com" className="flex items-center gap-2 text-gray-600 hover:text-teal-500 text-sm transition-colors cursor-pointer">
                <i className="ri-mail-line text-teal-500/70" />
                myfoodtruckagency@gmail.com
              </a>
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <i className="ri-map-pin-line text-teal-500/70" />
                {t('footer.address')}
              </div>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-5">{t('footer.officeHours')}</h4>
            <div className="flex flex-col gap-3">
              {hours.map((h) => (
                <div key={h.day}>
                  <div className="text-gray-900 text-sm font-medium">{h.day}</div>
                  <div className="text-gray-400 text-xs mt-0.5">{h.hours}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-5">{t('footer.quickLinks')}</h4>
            <div className="flex flex-col gap-3">
              {quickLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className="text-gray-600 hover:text-teal-500 text-sm text-left transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="mt-8">
              <button
                onClick={() => handleNav('#consultation')}
                className="w-full bg-teal-500 hover:bg-teal-600 text-gray-900 text-sm font-semibold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('footer.bookSession')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Wordmark */}
      <div className="border-t border-gray-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-gray-300 text-xs">
            &copy; {new Date().getFullYear()} {t('footer.copyright')}
          </p>
          <p className="text-gray-300 text-xs">
            {t('footer.serving')}
          </p>
        </div>
        <div className="text-center overflow-hidden px-4 pb-2">
          <div className="text-teal-100 text-[80px] md:text-[120px] lg:text-[160px] font-black tracking-tighter leading-none select-none whitespace-nowrap">
            Orange County
          </div>
        </div>
      </div>
    </footer>
  );
}
